const { isObject } = require('../../type');

/**
 * Creates a new in-memory Storage implementation.
 *
 * This storage fully implements the standard Web Storage API and can be used
 * as a drop-in replacement for `localStorage` or `sessionStorage`.
 *
 * @param {Object} [populateWithValues]
 * An optional object containing initial key/value pairs.
 *
 * @returns {Storage}
 * A new in-memory storage instance.
 *
 * @throws {TypeError}
 * If the initial value is not an object.
 */
function createInMemoryStorage(populateWithValues = {}) {
  if (arguments.length > 0 && !isObject(populateWithValues)) {
    throw new TypeError(
      'Storage can only be populated with a key/value object.'
    );
  }

  /** @type {Storage} */
  const memoryStorage = {
    items: populateWithValues,

    get length() {
      return Object.keys(this.items).length;
    },

    /**
     * Returns the value associated with the given key.
     *
     * @param {string} key
     * The key name.
     *
     * @returns {any}
     * The stored value, or `null` if the key does not exist.
     */
    getItem(key) {
      return this.items[key] ?? null;
    },

    /**
     * Returns the name of the nth key in the storage.
     *
     * @param {number} n
     * The zero-based index of the key.
     *
     * @returns {string|undefined}
     * The name of the nth key, or `undefined` if the index is out of range.
     */
    key(n) {
      return Object.keys(this.items)[n];
    },

    /**
     * Removes the key/value pair associated with the given key.
     *
     * @param {string} key
     * The key name.
     */
    removeItem(key) {
      delete this.items[key];
    },

    /**
     * Sets the value associated with the given key.
     *
     * If the key already exists, its value is updated.
     *
     * @param {string} key
     * The key name.
     *
     * @param {any} value
     * The value to store.
     */
    setItem(key, value) {
      this.items[key] = value;
    },

    /**
     * Removes all key/value pairs from the storage.
     */
    clear() {
      this.items = {};
    },
  };

  return memoryStorage;
}

module.exports = {
  createInMemoryStorage,
};
